import { useContext } from 'react';
import { brandRevision } from '@parsina/framework/ui/lib/brand';
import { I18nContext } from './I18nProvider';
import { productDefaultLocale, localeDescriptor } from './config';
import { createFormatters } from './format';
import { createTranslator } from './translator';
import { bindMoney, DEFAULT_CURRENCY } from './money';

// ─────────────────────────────────────────────────────────────────────────────
// The only way a component reads a string.
//
//   const { t, formatDate, isRTL } = useTranslation();
//   <button>{t('common.actions.save')}</button>
//
// Keys are always fully qualified — the first segment names the file the string
// lives in (see translator.js). There is no scoped/`useTranslation('visit')`
// form on purpose: one rule means a key you see in a screen is a key you can
// grep for, and a string cannot be reached by two different names.
// ─────────────────────────────────────────────────────────────────────────────

// Rendered outside a provider — a component under test, or an error boundary
// that caught the provider itself. It reports the product's default locale (the
// brand's, when it names one) and translates to the humanised key rather than
// throwing, because "no translations" must never be the reason a clinical
// screen fails to render. Rebuilt if the brand changes after the first build.
let orphan = null;
let orphanRevision = -1;
function orphanValue() {
  if (!orphan || orphanRevision !== brandRevision()) {
    orphanRevision = brandRevision();
    const locale = productDefaultLocale();
    const descriptor = localeDescriptor(locale);
    const formatters = createFormatters(locale);
    const { t, tList, has } = createTranslator({
      locale,
      catalog: {},
      fallbackCatalog: {},
      joinList: formatters.formatList,
      formatNumber: formatters.formatNumber,
    });
    // IRR, like the provider before a clinic is known: no clinic, no currency.
    const moneyBase = { t, tList, formatMoney: formatters.formatMoney, locale };
    orphan = {
      locale,
      dir: descriptor.dir,
      isRTL: descriptor.dir === 'rtl',
      readsPersianData: descriptor.readsPersianData === true,
      locales: {},
      t, tList, has,
      ...formatters,
      ...bindMoney(moneyBase, DEFAULT_CURRENCY),
      moneyBase,
      setLocale: () => {},
      preloadLocale: () => {},
      ready: false,
    };
  }
  return orphan;
}

export function useTranslation() {
  return useContext(I18nContext) ?? orphanValue();
}

export default useTranslation;
